import React, { useEffect, useState } from 'react';
import { isRouteErrorResponse, useRouteError, useNavigate } from 'react-router-dom';

const RouteErrorBoundary: React.FC = () => {
  const error = useRouteError();
  const navigate = useNavigate();
  const [hasAttemptedReload, setHasAttemptedReload] = useState(false);

  const isChunkError =
    error instanceof Error &&
    (error.message.includes('Loading chunk') ||
      error.message.includes('Loading CSS chunk') ||
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Unexpected token'));

  useEffect(() => {
    const lastErrorTimestamp = sessionStorage.getItem('lastRouteErrorTimestamp');
    const currentTime = Date.now();

    // Only attempt reload if more than 10 seconds have passed since the last error
    if (!lastErrorTimestamp || currentTime - parseInt(lastErrorTimestamp) > 10000) {
      sessionStorage.setItem('lastRouteErrorTimestamp', currentTime.toString());

      if (isChunkError && 'caches' in window) {
        caches.keys().then((names) => {
          names.forEach((name) => {
            caches.delete(name);
          });
        });
      }
      window.location.reload();
    } else {
      setHasAttemptedReload(true);
    }
  }, [isChunkError]);

  const getErrorMessage = () => {
    if (isRouteErrorResponse(error)) {
      return `${error.status} ${error.statusText}`;
    }
    if (error instanceof Error) {
      return error.message;
    }
    return 'Unknown error occurred';
  };

  const handleReload = () => {
    if (isChunkError && 'caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }
    window.location.reload();
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div
      style={{
        padding: '2rem',
        margin: '2rem auto',
        maxWidth: '600px',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        backgroundColor: '#fff',
      }}
    >
      <h2
        style={{
          color: '#d32f2f',
          marginBottom: '1rem',
        }}
      >
        {isChunkError ? 'Loading Error' : 'Page Error'}
      </h2>

      <p style={{ fontSize: '1.1rem', marginBottom: '1.5rem' }}>
        {isChunkError
          ? 'Failed to load this page. This might be due to a network issue or an outdated version.'
          : getErrorMessage()}
        <br />
        <span style={{ fontSize: '0.9rem', color: '#666' }}>
          {!hasAttemptedReload ? 'Attempting to reload automatically...' : ''}
        </span>
      </p>

      {!hasAttemptedReload ? (
        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
          <div className="loading-spinner" style={{ margin: '1rem auto' }}>
            ⌛
          </div>
        </div>
      ) : (
        <>
          {isChunkError && (
            <div style={{ marginBottom: '1.5rem' }}>
              <p>This might be caused by:</p>
              <ul style={{ marginLeft: '1.5rem', lineHeight: '1.6' }}>
                <li>Temporary network issues</li>
                <li>An outdated version of the application in your browser cache</li>
                <li>A new version of the application being deployed</li>
              </ul>
            </div>
          )}

          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              onClick={handleGoBack}
              style={{
                padding: '12px 24px',
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1rem',
              }}
            >
              Go Back
            </button>
            <button
              onClick={handleReload}
              style={{
                padding: '12px 24px',
                backgroundColor: '#2196f3',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1rem',
              }}
            >
              Try Reloading Again
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default RouteErrorBoundary;
